import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchWithAuth } from './authService';
import { API_ENDPOINTS } from '../utils/constants';

export default function TringboxWelcomeModal({
  onClose,
  onSuccess,
  updateUserAsync, // from useStatsigUser
}) {
  const [name, setName] = useState('');
  const [hoverLogo, setHoverLogo] = useState(false);
  const [hoverButton, setHoverButton] = useState(false);
  const [focusInput, setFocusInput] = useState(false);
  const [photoSrc, setPhotoSrc] = useState(null);
  const [rawFile, setRawFile] = useState(null);
  const [buttonPulsing, setButtonPulsing] = useState(true);
  const [buttonClickScale, setButtonClickScale] = useState(1);
  const [buttonText, setButtonText] = useState('Save & Continue');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const placeholders = useMemo(
    () => ['Enter your name', 'What should we call you?'],
    []
  );

  const photoInputRef = useRef(null);

  // Cycle placeholder
  useEffect(() => {
    const id = setInterval(() => {
      if (!name) setPlaceholderIndex(i => (i + 1) % placeholders.length);
    }, 3000);
    return () => clearInterval(id);
  }, [name, placeholders]);

  // Upload API
  const uploadImage = async file => {
    const formData = new FormData();
    formData.append('avatar', file);
    const token = localStorage.getItem('tringAuthToken');
    const response = await fetch(API_ENDPOINTS.USER_UPLOAD_AVATAR, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let message = 'Failed to upload image';
      try {
        const errorData = await response.json();
        message = errorData.message || message;
      } catch {
        // If response is not JSON, fallback to plain text
        try {
          message = await response.text();
        } catch {
          // ignore parsing errors
        }
      }
      throw new Error(message);
    }

    let data;
    try {
      data = await response.json();
    } catch {
      // If response is not JSON, fallback to plain text
      const textData = await response.text();
      throw new Error(`Unexpected response format: ${textData}`);
    }

    setAvatarUrl(data.avatar);
    return data.avatar;
  };

  // File input handler with preview + upload
  const onPhotoChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRawFile(file);
    // Show preview
    const reader = new FileReader();
    reader.onload = ev => setPhotoSrc(ev.target.result);
    reader.readAsDataURL(file);
    // Upload to get avatar URL
    setAvatarUploading(true);
    try {
      const url = await uploadImage(file);
      setAvatarUrl(url);
    } catch (err) {
      console.error('Image Upload Error:', err);
      alert(err.message || 'Failed to upload image');
      setAvatarUrl(null);
    } finally {
      setAvatarUploading(false);
    }
  };

  const putProfile = async (firstName, avatar) => {
    const token = localStorage.getItem('tringAuthToken');
    if (!token) throw new Error('Missing session');
    const payload = { first_name: firstName };
    if (avatar) payload.avatar = avatar;

    const resp = await fetchWithAuth(API_ENDPOINTS.USERS, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) throw new Error('Failed to update profile');
    return resp.json();
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setFocusInput(true);
      return;
    }
    setButtonPulsing(false);
    setButtonClickScale(0.98);

    setSubmitting(true);
    try {
      // ensure avatar upload completed; if user selected and it's still uploading, wait
      // here we simply rely on avatarUrl being set by onPhotoChange
      const userData = await putProfile(trimmed, avatarUrl || undefined);

      // pulse bounce
      setTimeout(() => {
        setButtonClickScale(1.05);
        setTimeout(() => setButtonClickScale(1), 200);
      }, 150);
      // Statsig update
      await updateUserAsync?.({
        userID: userData.id || localStorage.getItem('userId'),
        custom: { name: trimmed },
      });

      onSuccess?.({ name: trimmed, avatar: avatarUrl || null, user: userData });
      onClose();
    } catch (err) {
      console.error('Profile update error:', err);
      alert(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
      onClose();
    }
  };

  // Colors
  const crimsonGrad = 'linear-gradient(135deg, #8B0000, #DC143C)';

  // Styles (unchanged UI + modal overlay)
  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
      backdropFilter: 'blur(2px)',
    },
    root: {
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      height: '100vh',
      overflow: 'hidden',
      position: 'relative',
      borderRadius: 16,
      boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
    },
    bgMusic: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      opacity: 0.03,
      zIndex: 0,
      backgroundImage:
        'radial-gradient(circle at 20% 30%, transparent 20px, transparent 21px), radial-gradient(circle at 70% 60%, transparent 15px, transparent 16px), radial-gradient(circle at 40% 80%, transparent 25px, transparent 26px)',
      backgroundSize: '200px 200px, 150px 150px, 180px 180px',
      animation: 'musicFloat 20s ease-in-out infinite',
    },
    particles: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      zIndex: 1,
    },
    particleBase: {
      position: 'absolute',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '50%',
      animation: 'float 6s ease-in-out infinite',
    },
    container: {
      position: 'relative',
      zIndex: 2,
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
      background: 'rgba(245, 245, 220, 0.1)',
    },
    welcomeCard: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      borderRadius: 24,
      padding: '40px 30px',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
      textAlign: 'center',
      maxWidth: 360,
      width: '100%',
      border: '1px solid rgba(0, 0, 0,0.4)',
      animation: 'slideUp 0.8s ease-out',
      position: 'relative',
    },
    logo: {
      width: 80,
      height: 80,
      background: crimsonGrad,
      borderRadius: 24,
      margin: '0 auto 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 32,
      fontWeight: 'bold',
      color: 'white',
      boxShadow:
        '0 12px 24px rgba(220, 20, 60, 0.25), 0 4px 8px rgba(220, 20, 60, 0.15)',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      transform: hoverLogo ? 'translateY(-2px) scale(1.02)' : 'none',
    },
    logoBefore: {
      position: 'absolute',
      top: -2,
      left: -2,
      right: -2,
      bottom: -2,
      background: 'linear-gradient(45deg, #DC143C, #FF6B6B, #DC143C)',
      borderRadius: 26,
      opacity: hoverLogo ? 1 : 0,
      transition: 'opacity 0.3s ease',
      zIndex: -1,
    },
    h1: {
      fontSize: 28,
      fontWeight: 700,
      color: '#2d3748',
      marginBottom: 8,
      background: 'linear-gradient(135deg, #8B0000, #DC143C)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    subtitle: {
      color: '#718096',
      fontSize: 16,
      marginBottom: 30,
      lineHeight: 1.5,
    },
    photoUpload: { position: 'relative', marginBottom: 24 },
    photoCircle: {
      width: 100,
      height: 100,
      borderRadius: '50%',
      border: `3px ${photoSrc ? 'solid' : 'dashed'} ${
        photoSrc ? '#DC143C' : '#cbd5e0'
      }`,
      margin: '0 auto 12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #f8fafc, #edf2f7)',
      position: 'relative',
      overflow: 'hidden',
      transform: photoSrc ? 'scale(1)' : undefined,
    },
    cameraIcon: {
      width: 32,
      height: 32,
      color: hoverLogo ? '#DC143C' : '#a0aec0',
      transition: 'color 0.3s ease',
    },
    photoLabel: { fontSize: 14, color: '#718096', fontWeight: 500 },
    inputContainer: { position: 'relative', width: '100%', marginBottom: 20 },
    nameInput: {
      width: '100%',
      padding: '16px 20px 16px 50px',
      border: `2px solid ${focusInput ? '#DC143C' : '#e2e8f0'}`,
      borderRadius: 12,
      fontSize: 16,
      fontWeight: 500,
      color: '#2d3748',
      background: 'white',
      transition: 'all 0.3s ease',
      outline: 'none',
      boxShadow: focusInput ? '0 0 0 3px rgba(220, 20, 60, 0.1)' : 'none',
    },
    inputIcon: {
      position: 'absolute',
      left: 18,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 20,
      height: 20,
      color: focusInput ? '#DC143C' : '#a0aec0',
      transition: 'color 0.3s ease',
      pointerEvents: 'none',
    },
    ctaButton: {
      width: '100%',
      padding: 16,
      background: crimsonGrad,
      color: 'white',
      border: 'none',
      borderRadius: 12,
      fontSize: 16,
      fontWeight: 600,
      cursor: submitting ? 'not-allowed' : 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: hoverButton
        ? '0 8px 25px rgba(220, 20, 60, 0.4), 0 0 20px rgba(220, 20, 60, 0.3)'
        : '0 4px 15px rgba(220, 20, 60, 0.3), 0 0 0 0 rgba(220, 20, 60, 0.5)',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      transform: `scale(${buttonClickScale})`,
      animation:
        buttonPulsing && !hoverButton
          ? `pulseGlow ${name.trim() ? 1.5 : 2}s ease-in-out infinite`
          : 'none',
      opacity: submitting ? 0.9 : 1,
    },
    buttonArrow: {
      display: 'inline-block',
      transition: 'transform 0.3s ease',
      fontSize: 18,
      transform: hoverButton ? 'translateX(4px)' : 'none',
    },
    hiddenInput: { display: 'none' },
    sparkle: {
      position: 'absolute',
      color: '#ffd700',
      fontSize: 20,
      animation: 'sparkle 2s ease-in-out infinite',
    },
    uploadedPhoto: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: '50%',
    },
  };

  const particleStyles = [
    { width: 20, height: 20, left: '10%', animationDelay: '0s' },
    { width: 15, height: 15, left: '20%', animationDelay: '1s' },
    { width: 25, height: 25, left: '70%', animationDelay: '2s' },
    { width: 18, height: 18, left: '80%', animationDelay: '3s' },
    { width: 12, height: 12, left: '60%', animationDelay: '4s' },
  ];

  const shimmerChild = (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background:
          'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
        animation: 'shimmer 3s ease-in-out infinite',
        pointerEvents: 'none',
      }}
    />
  );

  return (
    <div style={styles.overlay}>
      <style>
        {`
          @keyframes musicFloat { 0%, 100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-20px) rotate(5deg); } }
          @keyframes musicNotes { 0% { transform: translateX(-100px); } 100% { transform: translateX(100px); } }
          @keyframes float { 0%, 100% { transform: translateY(100vh) rotate(0deg); opacity: 0; } 10%, 90% { opacity: 1; } 50% { transform: translateY(-20px) rotate(180deg); } }
          @keyframes slideUp { from { opacity: 0; transform: translateY(50px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes pulseGlow {
            0%, 100% { transform: scale(1); box-shadow: 0 4px 15px rgba(220, 20, 60, 0.3), 0 0 0 0 rgba(220, 20, 60, 0.5); }
            50% { transform: scale(1.02); box-shadow: 0 6px 20px rgba(220, 20, 60, 0.4), 0 0 15px rgba(220, 20, 60, 0.6); }
          }
          @keyframes shimmer { 0% { left: -100%; } 100% { left: 100%; } }
          @keyframes sparkle { 0%, 100% { opacity: 0; transform: scale(0.5) rotate(0deg); } 50% { opacity: 1; transform: scale(1) rotate(180deg); } }
          .bg-music-notes::before {
            content: '♪ ♫ ♪ ♬ ♫ ♪ ♬ ♫ ♪ ♬ ♫ ♪ ♬ ♫ ♪ ♬ ♫ ♪ ♬ ♫ ♪ ♬ ♫ ♪ ♬ ♫ ♪ ♬ ♫ ♪';
            position: absolute; top: 0; left: 0; width: 100%; height: 100%;
            font-size: 24px; color: rgba(139, 0, 0, 0.05); letter-spacing: 40px; line-height: 80px;
            animation: musicNotes 25s linear infinite;
          }
          @media (max-width: 768px) {
            .tb-container { padding: 15px !important; justify-content: flex-start !important; padding-top: 80px !important; }
            .tb-card { padding: 35px 25px !important; border-radius: 20px !important; box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15) !important; }
            .tb-logo { width: 70px !important; height: 70px !important; font-size: 28px !important; }
            .tb-title { font-size: 24px !important; }
            .tb-photo-circle { width: 90px !important; height: 90px !important; }
            .tb-cta { padding: 18px !important; font-size: 17px !important; font-weight: 700 !important; border-radius: 14px !important; touch-action: manipulation !important; }
          }
          @media (max-width: 375px) {
            .tb-card { padding: 30px 20px !important; margin: 10px !important; }
            .tb-container { padding-top: 60px !important; }
          }
        `}
      </style>

      <div className="tb-card" style={styles.welcomeCard}>
        <div
          style={{
            ...styles.sparkle,
            top: '10%',
            left: '5%',
            animationDelay: '0s',
          }}
        >
          ✨
        </div>
        <div
          style={{
            ...styles.sparkle,
            bottom: '39%',
            left: '10%',
            animationDelay: '1s',
          }}
        >
          ⭐
        </div>
        <div
          style={{
            ...styles.sparkle,
            top: '30%',
            right: '5%',
            animationDelay: '0.5s',
          }}
        >
          ✨
        </div>

        {/* <div
              className="tb-logo"
              onMouseEnter={() => setHoverLogo(true)}
              onMouseLeave={() => setHoverLogo(false)}
              style={styles.logo}
            >
              <div aria-hidden style={styles.logoBefore} />T
            </div> */}

        <h1 className="tb-title" style={styles.h1}>
          Welcome, Rockstar! 🎸
        </h1>
        <p style={styles.subtitle}>
          Almost there! Enter your name to unlock music and celebration
        </p>

        <div style={styles.photoUpload}>
          <div
            className="tb-photo-circle"
            style={styles.photoCircle}
            onClick={() => photoInputRef.current?.click()}
            title={avatarUploading ? 'Uploading...' : 'Add your photo'}
          >
            {photoSrc ? (
              <img
                alt="Uploaded avatar"
                src={photoSrc}
                style={styles.uploadedPhoto}
              />
            ) : (
              <svg
                style={styles.cameraIcon}
                fill="currentColor"
                viewBox="0 0 24 24"
                width="32"
                height="32"
              >
                <path d="M12 9a3 3 0 100 6 3 3 0 000-6zm0-2a5 5 0 110 10 5 5 0 010-10zm0-4a1 1 0 00-.707.293L9.586 5H7a3 3 0 00-3 3v8a3 3 0 003 3h10a3 3 0 003-3V8a3 3 0 00-3-3h-2.586l-1.707-1.707A1 1 0 0012 3z" />
              </svg>
            )}
          </div>
          <p style={styles.photoLabel}>
            {avatarUploading ? 'Uploading photo…' : 'Add your photo (optional)'}
          </p>
        </div>

        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          onChange={onPhotoChange}
          style={styles.hiddenInput}
        />

        <div style={styles.inputContainer}>
          <input
            type="text"
            required
            autoCapitalize="words"
            placeholder={placeholders[placeholderIndex]}
            value={name}
            onChange={e => setName(e.target.value)}
            onFocus={() => setFocusInput(true)}
            onBlur={() => setFocusInput(false)}
            style={styles.nameInput}
          />
          <svg style={styles.inputIcon} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
        </div>

        <button
          className="tb-cta"
          onMouseEnter={() => setHoverButton(true)}
          onMouseLeave={() => setHoverButton(false)}
          onClick={handleSubmit}
          disabled={submitting}
          style={styles.ctaButton}
        >
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background:
                'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              animation: 'shimmer 3s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
          <span>{buttonText}</span>
          <span style={styles.buttonArrow}>→</span>
        </button>
      </div>
    </div>
  );
}
